/** Date-only recurrence. Calendar days are never millisecond durations. */
import { t, formatDate } from '../../shared/i18n/index.js';
export function dayKey(date = new Date(), zone = null) {
  if (zone) {
    const parts = new Intl.DateTimeFormat('en-CA', {timeZone:zone,year:'numeric',month:'2-digit',day:'2-digit'}).formatToParts(date);
    const get = key => parts.find(p => p.type === key).value;
    return `${get('year')}-${get('month')}-${get('day')}`;
  }
  return `${date.getFullYear()}-${String(date.getMonth()+1).padStart(2,'0')}-${String(date.getDate()).padStart(2,'0')}`;
}
export function validDay(value) {
  return typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value) &&
    Number.isFinite(Date.parse(value)) && new Date(`${value}T12:00:00Z`).toISOString().slice(0,10) === value;
}
const date = value => new Date(`${value}T12:00:00Z`);
const iso = value => { const year=value.getUTCFullYear(); return Number.isFinite(year)&&year>=1&&year<=9999?value.toISOString().slice(0,10):null; };
export function addDays(day, n) { const d=date(day); d.setUTCDate(d.getUTCDate()+n); return iso(d); }
export function addInterval(day, count, unit, monthEnd = 'clamp') {
  if (unit === 'days' || unit === 'weeks') return addDays(day,count*(unit === 'weeks' ? 7 : 1));
  const d=date(day), original=d.getUTCDate();
  d.setUTCDate(1); d.setUTCMonth(d.getUTCMonth()+count*(unit === 'years' ? 12 : 1));
  const end=new Date(Date.UTC(d.getUTCFullYear(),d.getUTCMonth()+1,0,12)).getUTCDate();
  if (monthEnd === 'skip' && original>end) return null;
  d.setUTCDate(monthEnd === 'last' ? end : Math.min(original,end));
  return iso(d);
}
export function normalizeSchedule(raw) {
  if (!raw || raw.mode === 'none') return null;
  if (!['calendar','completion'].includes(raw.mode) || !validDay(raw.start)) throw Error(t('routines.error.firstDate'));
  const interval=Number(raw.interval);
  if (!Number.isInteger(interval) || interval<1 || interval>1000) throw Error(t('routines.error.interval'));
  if (!['days','weeks','months','years'].includes(raw.unit)) throw Error(t('routines.error.unit'));
  if (raw.until && (!validDay(raw.until) || raw.until<raw.start)) throw Error(t('routines.error.until'));
  const zone=raw.zone || Intl.DateTimeFormat().resolvedOptions().timeZone;
  try {new Intl.DateTimeFormat('en',{timeZone:zone});} catch {throw Error(t('routines.error.timeZone'));}
  if(raw.allowedDays !== undefined && (!Array.isArray(raw.allowedDays) || !raw.allowedDays.length || raw.allowedDays.some(n=>!Number.isInteger(n)||n<0||n>6))) throw Error(t('routines.error.allowedDay'));
  return {mode:raw.mode,start:raw.start,interval,unit:raw.unit,zone,
    ...(raw.allowedDays ? {allowedDays:[...new Set(raw.allowedDays)].sort()} : {}),
    until:raw.until || null, weekdays:[...new Set((raw.weekdays || []).map(Number).filter(n=>Number.isInteger(n)&&n>=0&&n<7))].sort(),
    monthEnd:['clamp','skip','last'].includes(raw.monthEnd) ? raw.monthEnd : 'clamp',
    missed:raw.missed === 'keep' ? 'keep' : 'continue'};
}
/** Short weekday name in the app language (0 = Sunday): "Sun", "Mon"… in English. */
export function weekdayLabel(n) {
  return formatDate(new Date(Date.UTC(2023,0,1+n)), {weekday:'short', timeZone:'UTC'}); // 2023-01-01 was a Sunday.
}
const SINGLE={days:'daily',weeks:'weekly',months:'monthly',years:'yearly'};
/** "Every day", "Every 2 weeks after completion · Mon, Wed"… in the app language. */
export function scheduleText(s) {
  if (!s) return t('routines.schedule.whenever');
  const after=s.mode==='completion'?'After':'';
  const rule=s.interval===1 ? t(`routines.schedule.${SINGLE[s.unit]}${after}`) : t(`routines.schedule.${s.unit}${after}`, {count:s.interval});
  const list=days=>` · ${days.map(weekdayLabel).join(t('routines.schedule.daySeparator'))}`;
  const allowed=s.allowedDays?.length<7 ? list(s.allowedDays) : '';
  const days=s.unit==='weeks' && s.weekdays?.length ? list(s.weekdays) : '';
  return `${rule}${days}${allowed}`;
}
/** Bounded projection; direct interval jumping handles years away efficiently. */
export function nextAllowedDay(day, schedule) {
  if(!day || !schedule?.allowedDays)return day;
  for(let i=0;i<7;i++){const candidate=addDays(day,i);if(candidate && schedule.allowedDays.includes(date(candidate).getUTCDay()))return candidate;}
  return null;
}
export function calendarDates(s, from, to, limit = 400) {
  if (!s || s.mode!=='calendar' || !validDay(from) || !validDay(to) || from>to) return [];
  const requestedFrom=from;
  if(s.allowedDays)from=addDays(from,-6)||from;
  from=from<s.start?s.start:from; to=s.until && to>s.until?s.until:to;
  const out=[];
  const accept=nominal=>{const day=nextAllowedDay(nominal,s);if(day&&day>=requestedFrom&&day<=to&&!out.includes(day))out.push(day);};
  if (s.unit==='weeks' && s.weekdays.length) {
    const anchor=addDays(s.start,-((date(s.start).getUTCDay()+6)%7));
    let week=Math.max(0,Math.floor((date(from)-date(anchor))/(86400000*7*s.interval)));
    for (let tries=0;tries<limit*8+16;tries++,week++) {
      const start=addDays(anchor,week*7*s.interval); if(!start||start>to)break;
      for(let i=0;i<7;i++) {const d=addDays(start,i); if(d&&d>=from&&d<=to&&s.weekdays.includes(date(d).getUTCDay()))accept(d);}
      if(out.length>=limit)break;
    }
  } else {
    const a=date(s.start), b=date(from);
    const distance=s.unit==='months'||s.unit==='years' ? (b.getUTCFullYear()-a.getUTCFullYear())*12+b.getUTCMonth()-a.getUTCMonth() : (b-a)/86400000;
    const divisor=s.interval*(s.unit==='years'?12:s.unit==='weeks'?7:1);
    let n=Math.max(0,Math.floor(distance/divisor)-1);
    for(let tries=0;tries<limit*8+16;tries++,n++) {
      if(!addInterval(s.start,n*s.interval,s.unit,'clamp'))break;
      const d=addInterval(s.start,n*s.interval,s.unit,s.monthEnd);
      if(!d)continue; if(d>to)break; if(d>=from)accept(d); if(out.length>=limit)break;
    }
  }
  return out.slice(0,limit);
}
